import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { merchantApi } from '../api/config';
import { Stats, Order } from '../types';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine, faClipboardList, faHourglassHalf, faCheckCircle, faXmark,
  faChartPie, faMoneyBill, faWallet, faHandHoldingDollar, faMoneyBillTransfer,
  faCoins, faReceipt, faBan, faPercentage, faArrowTrendUp, faSackDollar,
  faChevronRight, faPiggyBank, faInbox,
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { fmtBaht, fmtMoney, fmtNum } from '../utils/formatMoney';
import bankLogos from 'thai-banks-logo/banks-logo.json';
import { UiPageHeader, UiFilterPanel, UiStatsGrid, StatusBadge } from '../components/ui';
import type { StatCardDef, StatusDef } from '../components/ui';
import { DataTable } from '../components/shared';
import type { Column } from '../components/shared';
import { usePermissions } from '../hooks/usePermissions';
import P2pReportPanel, { P2pReportSummary } from '../components/P2pReportPanel';

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = 'Asia/Bangkok';
const monthStart = () => dayjs().tz(TZ).startOf('month').format('YYYY-MM-DD');
const monthEnd = () => dayjs().tz(TZ).endOf('month').format('YYYY-MM-DD');

/* ── Status maps (StatusBadge) ────────────────────────────────── */
const ORDER_STATUS_MAP: Record<string, StatusDef> = {
  paid:      { bg: 'bg-green-100', text: 'text-green-700', icon: faCheckCircle, label: 'paid' },
  pending:   { bg: 'bg-amber-100', text: 'text-amber-700', icon: faHourglassHalf, label: 'pending' },
  expired:   { bg: 'bg-red-100', text: 'text-red-600', icon: faXmark, label: 'expired' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-600', icon: faBan, label: 'cancelled' },
};
const PAYOUT_STATUS_MAP: Record<string, StatusDef> = {
  completed: { bg: 'bg-green-100', text: 'text-green-700', icon: faCheckCircle, label: 'completed' },
  approved:  { bg: 'bg-indigo-100', text: 'text-indigo-700', icon: faCheckCircle, label: 'approved' },
  pending:   { bg: 'bg-amber-100', text: 'text-amber-700', icon: faHourglassHalf, label: 'pending' },
  rejected:  { bg: 'bg-red-100', text: 'text-red-600', icon: faXmark, label: 'rejected' },
};

/* ── Bank logo helper ─────────────────────────────────────────── */
const bankIcon = (name?: string) => {
  const key = (name || '').toUpperCase()
    .replace('CIMBT', 'CIMB').replace('UOBT', 'UOB').replace('LH BANK', 'LHB') as keyof typeof bankLogos;
  return bankLogos[key];
};

const BankCell: React.FC<{ name?: string; title?: string; subtitle?: string }> = ({ name, title, subtitle }) => {
  const bank = bankIcon(name);
  return (
    <div className="flex items-center gap-2">
      {bank
        ? <img src={bank.icon} alt={name || ''} className="w-7 h-7 rounded-full object-contain flex-shrink-0" />
        : <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-slate-600 flex-shrink-0" />}
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-700 dark:text-slate-200 truncate">{title || '-'}</div>
        <div className="text-xs text-gray-400 dark:text-slate-500 truncate">{subtitle || '-'}</div>
      </div>
    </div>
  );
};

const DateCell: React.FC<{ value: string }> = ({ value }) => (
  <div>
    <p className="text-sm text-gray-700 dark:text-slate-200">{dayjs(value).tz(TZ).format('DD/MM/YYYY')}</p>
    <p className="text-xs text-gray-400 dark:text-slate-500">{dayjs(value).tz(TZ).format('HH:mm')}</p>
  </div>
);

const MetricTile = ({ label, value, valueClass, icon, sub }: {
  label: string; value: string | number; valueClass: string; icon: any; sub?: React.ReactNode;
}) => (
  <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3 sm:p-4 flex flex-col gap-1 cursor-default
                  transition-all duration-200 hover:-translate-y-1 hover:shadow-md group">
    <div className="flex items-center gap-2 text-gray-400 dark:text-slate-500 text-xs font-medium">
      <FontAwesomeIcon icon={icon} className="text-[10px] transition-all duration-300 group-hover:scale-125 group-hover:rotate-12" />
      <span className="transition-colors duration-200 group-hover:text-gray-600 dark:group-hover:text-slate-400">{label}</span>
    </div>
    <p className={`break-words text-lg font-bold leading-tight transition-all duration-200 group-hover:scale-105 origin-left sm:text-xl ${valueClass}`}>
      {value}
    </p>
    {sub && (
      <p className="text-[11px] leading-tight text-gray-400 dark:text-slate-500">{sub}</p>
    )}
  </div>
);

const StatRow = ({ label, value, valueClass }: {
  label: string; value: string | number; valueClass: string;
}) => (
  <div className="flex flex-col gap-2 rounded-lg bg-gray-50 dark:bg-slate-700/50 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between
                  border-l-2 border-gray-200 dark:border-slate-600 cursor-default transition-all duration-200
                  hover:bg-blue-50 dark:hover:bg-slate-700 hover:border-blue-400 hover:px-4 group">
    <span className="text-gray-500 dark:text-slate-400 font-medium text-sm transition-colors duration-200 group-hover:text-gray-700 dark:group-hover:text-slate-200">
      {label}
    </span>
    <div className="flex w-full items-center justify-between gap-1.5 sm:w-auto sm:justify-end">
      <strong className={`text-sm font-bold transition-all duration-200 group-hover:scale-105 inline-block ${valueClass}`}>
        {value}
      </strong>
      <FontAwesomeIcon icon={faChevronRight}
        className="text-[8px] text-blue-400 opacity-0 -translate-x-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0" />
    </div>
  </div>
);

const TileSkeleton = () => (
  <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4">
    <div className="h-3 w-20 mb-2 rounded bg-gray-200 dark:bg-slate-600 animate-pulse" />
    <div className="h-7 w-28 rounded bg-gray-200 dark:bg-slate-600 animate-pulse" />
  </div>
);

/* ── Main Component ───────────────────────────────────────────── */
const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canViewOrders = can('can_view_orders');
  const canViewPayouts = can('can_view_payouts');
  const [stats, setStats] = useState<Stats | null>(null);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [recentPayouts, setRecentPayouts] = useState<any[]>([]);
  const [wallet, setWallet] = useState<any>(null);
  const [p2p, setP2p] = useState<P2pReportSummary | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [payoutsLoading, setPayoutsLoading] = useState(true);

  // Draft (input) vs applied (API) — date only applies after pressing ค้นหา
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(monthEnd);
  const [appliedDateFrom, setAppliedDateFrom] = useState(monthStart);
  const [appliedDateTo, setAppliedDateTo] = useState(monthEnd);

  const loadStats = useCallback(async () => {
    try {
      let statsUrl = '/merchants/stats';
      if (appliedDateFrom && appliedDateTo) {
        statsUrl += `?start_date=${appliedDateFrom}&end_date=${appliedDateTo}`;
      }
      const p2pParams = (appliedDateFrom && appliedDateTo)
        ? { date_from: dayjs(appliedDateFrom).startOf('day').toISOString(), date_to: dayjs(appliedDateTo).endOf('day').toISOString() }
        : {};
      const [statsRes, walletRes, p2pRes] = await Promise.all([
        merchantApi.get(statsUrl),
        merchantApi.get('/merchants/wallet').catch(() => ({ data: { data: { balance: 0 } } })),
        merchantApi.get('/merchants/p2p-report', { params: p2pParams }).catch(() => null),
      ]);
      setStats(statsRes.data.data);
      setWallet(walletRes.data.data);
      setP2p(p2pRes?.data?.success ? (p2pRes.data.data.summary ?? null) : null);
    } catch {
      if (statsLoading) toast.error(t('dashboard.statsLoadError'));
    } finally {
      setStatsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedDateFrom, appliedDateTo]);

  const loadOrders = useCallback(async () => {
    if (!canViewOrders) { setOrdersLoading(false); return; }
    try {
      const res = await merchantApi.get('/merchants/orders?limit=10');
      setRecentOrders(res.data.data);
    } catch { /* silent */ } finally {
      setOrdersLoading(false);
    }
  }, [canViewOrders]);

  const loadPayouts = useCallback(async () => {
    if (!canViewPayouts) { setPayoutsLoading(false); return; }
    try {
      const res = await merchantApi.get('/merchants/payouts?limit=10');
      setRecentPayouts(res.data.data?.payouts || []);
    } catch { /* silent */ } finally {
      setPayoutsLoading(false);
    }
  }, [canViewPayouts]);

  useEffect(() => {
    loadStats();
    loadOrders();
    loadPayouts();
    const interval = setInterval(() => { loadStats(); loadOrders(); loadPayouts(); }, 30000);
    return () => clearInterval(interval);
  }, [loadStats, loadOrders, loadPayouts]);

  const handleSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setStatsLoading(true);
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
  };

  const handleReset = () => {
    const s = monthStart();
    const e = monthEnd();
    setDateFrom(s);
    setDateTo(e);
    setStatsLoading(true);
    setAppliedDateFrom(s);
    setAppliedDateTo(e);
  };

  const successRate = stats && stats.total_orders > 0
    ? ((stats.paid / stats.total_orders) * 100).toFixed(1) : '0';

  /* ── Stat cards ─────────────────────────────────────────────── */
  const statCards: StatCardDef[] = stats ? [
    {
      icon: faClipboardList, iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'Total Orders',
      value: <span className="text-gray-800 dark:text-slate-100">{fmtNum(stats.total_orders)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">คำสั่งซื้อทั้งหมด</span>,
    },
    {
      icon: faHourglassHalf, iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'Pending',
      value: <span className="text-amber-600 dark:text-amber-400">{stats.pending}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">รอชำระเงิน</span>,
    },
    {
      icon: faCheckCircle, iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'Paid',
      value: <span className="text-green-600 dark:text-green-400">{stats.paid}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ชำระสำเร็จ</span>,
    },
    {
      icon: faXmark, iconClassName: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'Expired',
      value: <span className="text-red-500 dark:text-red-400">{stats.expired || 0}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">หมดอายุ</span>,
    },
    {
      icon: faCoins, iconClassName: 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400',
      label: 'Total Revenue',
      value: <span className="text-gray-800 dark:text-slate-100">{fmtBaht(stats.total_revenue)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ยอดรับทั้งหมด</span>,
    },
    {
      icon: faHandHoldingDollar, iconClassName: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
      label: 'Net Revenue',
      value: <span style={{ color: 'var(--theme-accent)' }}>{fmtBaht(stats.net_revenue || 0)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">หลังหักค่าธรรมเนียม</span>,
    },
    {
      icon: faWallet, iconClassName: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      label: 'Main Wallet',
      value: <span className="text-gray-800 dark:text-slate-100">{fmtBaht(wallet?.balance || 0)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ยอดคงเหลือ</span>,
    },
    {
      icon: faMoneyBillTransfer, iconClassName: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
      label: 'Payout Wallet',
      value: <span className="text-indigo-600 dark:text-indigo-400">{fmtBaht(wallet?.payout_balance || 0)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ยอดคงเหลือ (Payout)</span>,
    },
  ] : [];

  /* ── Table columns ──────────────────────────────────────────── */
  const orderColumns: Column<Order>[] = [
    {
      key: 'order_number', header: 'Order #',
      render: (o) => <span className="font-mono text-sm font-semibold text-gray-700 dark:text-slate-200">{o.order_number}</span>,
    },
    {
      key: 'bank', header: t('order.bankColumn'),
      render: (o) => <BankCell name={o.bank_name} title={o.customer_name || undefined} subtitle={o.bank_number || undefined} />,
    },
    {
      key: 'amount', header: t('common.amount'),
      render: (o) => <span className="text-sm font-bold" style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(o.total_amount)}</span>,
    },
    {
      key: 'customer', header: t('order.customer'),
      render: (o) => <span className="text-sm text-gray-500 dark:text-slate-400">{o.customer_name || '–'}</span>,
    },
    {
      key: 'status', header: t('common.status'),
      render: (o) => <StatusBadge status={o.status} statusMap={ORDER_STATUS_MAP} />,
    },
    {
      key: 'created_at', header: t('order.createdAt'),
      render: (o) => <DateCell value={o.created_at} />,
    },
  ];

  const payoutColumns: Column<any>[] = [
    {
      key: 'payout_number', header: 'Payout #',
      render: (p) => <span className="font-mono text-sm font-semibold text-gray-700 dark:text-slate-200">{p.payout_number}</span>,
    },
    {
      key: 'bank', header: t('order.bankColumn'),
      render: (p) => <BankCell name={p.bank_name} title={p.bank_account_name} subtitle={p.bank_account_number} />,
    },
    {
      key: 'amount', header: t('common.amount'),
      render: (p) => <span className="text-sm font-bold text-purple-600 dark:text-purple-400">฿{fmtMoney(p.amount)}</span>,
    },
    {
      key: 'status', header: t('common.status'),
      render: (p) => <StatusBadge status={p.status} statusMap={PAYOUT_STATUS_MAP} />,
    },
    {
      key: 'created_at', header: t('order.createdAt'),
      render: (p) => <DateCell value={p.created_at} />,
    },
  ];

  return (
    <div className="w-full">
      <UiPageHeader
        icon={faChartLine}
        title="Merchant Dashboard"
        subtitle={`${fmtNum(stats?.total_orders ?? 0)} คำสั่งซื้อ • ${appliedDateFrom} – ${appliedDateTo}`}
      />

      {/* ── Filter (date applies on ค้นหา) ─────────────────────── */}
      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={statsLoading}
      />

      {/* ── Stat cards ─────────────────────────────────────────── */}
      <UiStatsGrid cards={statCards} loading={statsLoading} cols={4} />

      <P2pReportPanel summary={p2p} showMerchantCount={false} />

      {/* ── Revenue Breakdown + Order Status ───────────────────── */}
      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
        {/* Revenue Breakdown (2 cols) */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl shadow-theme border border-gray-100 dark:border-slate-700 overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-gray-100 dark:border-slate-700 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <h2 className="text-sm font-bold text-gray-700 dark:text-slate-200 flex items-center gap-2">
              <FontAwesomeIcon icon={faArrowTrendUp} style={{ color: 'var(--theme-primary)' }} />
              {t('dashboard.revenueSummary')}
            </h2>
            <span className="inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1 text-xs font-semibold
                             bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
              <FontAwesomeIcon icon={faPercentage} className="text-[10px]" />
              Success {successRate}%
            </span>
          </div>
          <div className="p-4 sm:p-5">
            {statsLoading ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => <TileSkeleton key={i} />)}
              </div>
            ) : stats && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <MetricTile icon={faCoins} label={t('dashboard.baseRevenue')}
                  value={fmtBaht(stats.base_revenue)} valueClass="text-blue-600 dark:text-blue-400" />
                <MetricTile icon={faMoneyBill} label={t('dashboard.actualPaid')}
                  value={fmtBaht(stats.total_revenue)} valueClass="text-violet-600 dark:text-violet-400" />
                <MetricTile icon={faReceipt} label={t('dashboard.centDifference')}
                  value={`${(stats.cent_difference || 0) >= 0 ? '+' : ''}${fmtBaht(stats.cent_difference || 0)}`}
                  valueClass="text-yellow-600 dark:text-yellow-400" />
                <MetricTile icon={faSackDollar} label={t('dashboard.totalFee')}
                  value={`-${fmtBaht((stats.total_fee || 0) + (stats.total_payout_fee || 0) + (stats.total_topup_fee || 0))}`}
                  valueClass="text-red-600 dark:text-red-400"
                  sub={((stats.total_fee || 0) > 0 || (stats.total_payout_fee || 0) > 0 || (stats.total_topup_fee || 0) > 0)
                    ? [
                        `${t('dashboard.feeDeposit')} ${fmtBaht(stats.total_fee || 0)}`,
                        `${t('dashboard.feePayout')} ${fmtBaht(stats.total_payout_fee || 0)}`,
                        // fee เติม payout โชว์เฉพาะเมื่อมีจริง — ร้านส่วนใหญ่ไม่ได้ใช้เส้นเติมด้วยสลิป
                        ...((stats.total_topup_fee || 0) > 0 ? [`${t('dashboard.feeTopup')} ${fmtBaht(stats.total_topup_fee || 0)}`] : []),
                      ].join(' · ')
                    : undefined} />
                {(stats.total_p2p_fee || 0) > 0 && (
                  <MetricTile icon={faSackDollar} label="ค่าธรรมเนียม P2P"
                    value={`-${fmtBaht(stats.total_p2p_fee || 0)}`} valueClass="text-sky-600 dark:text-sky-400" />
                )}
                <MetricTile icon={faHandHoldingDollar} label={t('dashboard.netReceived')}
                  value={fmtBaht(stats.net_revenue || 0)} valueClass="text-emerald-600 dark:text-emerald-400" />
                <MetricTile icon={faChartPie} label="Success Rate"
                  value={`${successRate}%`} valueClass="text-indigo-600 dark:text-indigo-400" />
              </div>
            )}
          </div>
        </div>

        {/* Order Status (1 col) */}
        <div className="rounded-xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-theme sm:p-5">
          <h2 className="text-sm font-bold text-gray-700 dark:text-slate-200 mb-3 pb-2 border-b border-gray-100 dark:border-slate-700 flex items-center gap-2">
            <FontAwesomeIcon icon={faClipboardList} style={{ color: 'var(--theme-primary)' }} className="text-xs" />
            {t('dashboard.orderStatus')}
          </h2>
          {statsLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-12 rounded-lg bg-gray-100 dark:bg-slate-700/50 animate-pulse" />
              ))}
            </div>
          ) : stats && (
            <div className="flex flex-col gap-2">
              <StatRow label={t('dashboard.pendingPayment')} value={`${stats.pending} Orders`} valueClass="text-amber-600 dark:text-amber-400" />
              <StatRow label={t('dashboard.paidSuccess')} value={`${stats.paid} Orders`} valueClass="text-green-600 dark:text-green-400" />
              <StatRow label={t('dashboard.expiredLabel')} value={`${stats.expired || 0} Orders`} valueClass="text-red-500 dark:text-red-400" />
              <StatRow label={t('dashboard.cancelledLabel')} value={`${stats.cancelled || 0} Orders`} valueClass="text-gray-500 dark:text-slate-400" />
            </div>
          )}
        </div>
      </div>

      {/* ── Recent Orders + Recent Payouts ─────────────────────── */}
      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        {canViewOrders && (
        <DataTable<Order>
          data={recentOrders}
          columns={orderColumns}
          loading={ordersLoading}
          keyExtractor={(o) => o.id}
          emptyMessage={t('dashboard.noItems')}
          emptyIcon={<FontAwesomeIcon icon={faInbox} />}
          headerContent={
            <h2 className="text-sm font-bold text-gray-700 dark:text-slate-200 flex items-center gap-2">
              <FontAwesomeIcon icon={faClipboardList} style={{ color: 'var(--theme-primary)' }} className="text-xs" />
              Recent Orders
            </h2>
          }
        />
        )}

        {canViewPayouts && (
        <DataTable<any>
          data={recentPayouts}
          columns={payoutColumns}
          loading={payoutsLoading}
          keyExtractor={(p) => p.id}
          emptyMessage={t('dashboard.noItems')}
          emptyIcon={<FontAwesomeIcon icon={faPiggyBank} />}
          headerContent={
            <h2 className="text-sm font-bold text-gray-700 dark:text-slate-200 flex items-center gap-2">
              <FontAwesomeIcon icon={faPiggyBank} className="text-purple-500 dark:text-purple-400 text-xs" />
              Recent Payouts
            </h2>
          }
        />
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
